import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type BzTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

let nextId = 0;

/**
 * Wraps its content and shows `content` in a bubble on hover or keyboard
 * focus. Pure CSS: no overlay, no positioning JavaScript, no viewport-edge
 * flipping. That is a deliberate, stated limitation, not an oversight — a
 * tooltip that repositions itself off-screen needs the same floating layer
 * `bz-select` and `bz-menu` are waiting on `@angular/cdk` for. This one
 * covers the common case (a tooltip near the middle of the screen) for free.
 *
 * Wraps content in an element, same as the legacy `bringoz-tooltip` did — a
 * directive-based attribute API was considered and rejected because a plain
 * `@Directive` cannot carry its own styles, which would have pushed the
 * bubble's CSS into a global stylesheet every consumer has to remember to
 * import.
 *
 * Known gap: `aria-describedby` is not wired from the wrapped content to the
 * bubble, because the content is arbitrary and this component cannot assume
 * it is a single focusable element. A consumer with a specific accessibility
 * requirement should link it by hand via a template reference.
 */
@Component({
  selector: 'bz-tooltip',
  templateUrl: './bz-tooltip.html',
  styleUrl: './bz-tooltip.scss',
  host: {
    '[class]': 'hostClass()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzTooltip {
  readonly content = input.required<string>();
  readonly placement = input<BzTooltipPlacement>('top');

  protected readonly id = `bz-tooltip-${nextId++}`;

  protected readonly hostClass = computed(() => `bz-tooltip bz-tooltip--${this.placement()}`);
}
